import { useEffect, useRef } from 'react';
import { AssetPlatformType } from './AdCreativeSelectorCampaignView';

/** Cloudinary upload widget configuration interface */
interface CloudinaryUploadWidget {
    open: () => void;
}

/** Cloudinary window interface */
interface CloudinaryInterface {
    createUploadWidget: (
        config: typeof uwConfig,
        callback: (error: any, result: any) => void
    ) => CloudinaryUploadWidget;
}

/** Extend Window interface to include cloudinary */
declare global {
    interface Window {
        cloudinary: CloudinaryInterface;
    }
}

// Upload Widget Configuration
const uwConfig = {
    cloudName: "symphonyos",
    uploadPreset: "unsigned-assets",
    maxFiles: 5,
    sources: ['local', 'url', 'camera'],
    // resourceType: "video",
    minImageWidth: 500,
    clientAllowedFormats: ['image', 'video'],
    showPoweredBy: false,
    showCompletedButton: true,
    showUploadMoreButton: true,
    maxFileSize: 1000000000,
};

export interface CloudinaryUploadedFileType {
    id: string;
    batchId: string;
    asset_id: string;
    public_id: string;
    version: number;
    version_id: string;
    signature: string;
    width: number;
    height: number;
    format: string;
    resource_type: string;
    created_at: string;
    tags: string[];
    bytes: number;
    type: string;
    etag: string;
    placeholder: boolean;
    url: string;
    secure_url: string;
    folder: string;
    access_mode: string;
    existing: boolean;
    original_filename: string;
    path: string;
    thumbnail_url: string;
    platformType: AssetPlatformType.CLOUDINARY
}

/** Props for the CloudinaryUploadWidget component */
interface CloudinaryUploadWidgetProps {
    /** Callback function called when upload is successful */
    onUpload: (info: CloudinaryUploadedFileType) => void;
    /** Reference to the button that will trigger the upload widget */
    buttonRef?: React.RefObject<HTMLButtonElement>;
    /** ID of the button that will trigger the upload widget */
    buttonId: string;
    /** Maximum file size in bytes */
    maxFileSize?: number;
}

const CloudinaryUploadWidget = ({ onUpload, buttonId, buttonRef, maxFileSize }: CloudinaryUploadWidgetProps) => {
    const uploadWidgetRef = useRef<CloudinaryUploadWidget | null>(null);

    useEffect(() => {
        const initializeUploadWidget = () => {
            if (!window.cloudinary) return;
            
            if (uploadWidgetRef.current) return;

            uploadWidgetRef.current = window.cloudinary.createUploadWidget(
                {
                    ...uwConfig,
                    maxFileSize: maxFileSize ?? uwConfig.maxFileSize
                },
                (error, result) => {
                    if (!error && result && result.event === 'success') {
                        onUpload({
                            ...result.info,
                            platformType: AssetPlatformType.CLOUDINARY
                        });
                    }
                }
            );

            const handleUploadClick = () => {
                if (uploadWidgetRef.current) {
                    uploadWidgetRef.current.open();
                }
            };
            if (!buttonId) return;

            const buttonElement = document.getElementById(buttonId);
            if (!buttonElement) return;

            buttonElement.addEventListener('click', handleUploadClick);

            return () => {
                if (buttonElement) {
                    buttonElement.removeEventListener('click', handleUploadClick);
                }
            };
        };

        initializeUploadWidget();
    }, [buttonId, onUpload]);

    return null;
};

export default CloudinaryUploadWidget;
